/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import {
  BUTTON_FORMAT_SOFT,
  BUTTON_THEME_DEFAULT,
  Button,
  DataList,
  DataListItem,
  ErrorMessage,
  Loader,
} from '@squiz/sds';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AI_STRINGS } from '../../Constants/Strings.constants';
import { isGenerative } from '../../Utilities/formPredicates';
import { AiDataListProps } from './AiDataList.props';
import { AiDataListEmptyState } from './utils/AiDataListEmptyState';
import { mapPageResultItemsToListItems } from './utils/mapPageResultItemsToListItems';

/**
 * Component to list both conversation and generative data types
 */
export const AiDataList = (props: AiDataListProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const labels = useMemo(() => {
    return {
      availableTools: t(AI_STRINGS.availableTools),
      availableToolsDescription: t(AI_STRINGS.availableToolsDescription),
      availableToolsEmpty: t(AI_STRINGS.availableToolsEmpty),
      currentChatbots: t(AI_STRINGS.currentChatbots),
      currentChatbotsDescription: t(AI_STRINGS.currentChatbotsDescription),
      currentChatbotsEmpty: t(AI_STRINGS.currentChatbotsEmpty),
      errorText: t(AI_STRINGS.apiError),
      loadMore: t(AI_STRINGS.loadMore),
    };
  }, [t]);

  const [pageResult, error, state] = props.paginationObservable;
  const [items, setItems] = useState<Array<DataListItem>>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (pageResult) {
      const newItems = mapPageResultItemsToListItems(pageResult.items, navigate);
      setItems((prevItems) => [...prevItems, ...newItems]);
      setLoading(false);
    }
  }, [navigate, pageResult]);

  const nextPageCursor = pageResult?.links?.nextPage;

  const screenText = isGenerative(props.type)
    ? {
        description: labels.availableToolsDescription,
        emptyMessage: labels.availableToolsEmpty,
        header: labels.availableTools,
      }
    : {
        description: labels.currentChatbotsDescription,
        emptyMessage: labels.currentChatbotsEmpty,
        header: labels.currentChatbots,
      };

  if (state === 'error' && error) {
    const errorMessage = <ErrorMessage message={labels.errorText} />;
    return (
      <div className="ai-data-list">
        <div className="list-header">
          <h4 className="title">{screenText.header}</h4>
          <p>{screenText.description}</p>
        </div>
        <DataList empty={errorMessage} isInteractive items={items}></DataList>
      </div>
    );
  }
  if (items.length) {
    return (
      <div className="ai-data-list">
        <div className="list-header">
          <h4 className="title">{screenText.header}</h4>
          <p>{screenText.description}</p>
        </div>
        <DataList empty={screenText.emptyMessage} isInteractive items={items}></DataList>
        {nextPageCursor ? (
          <div className="button-container">
            <Button
              format={BUTTON_FORMAT_SOFT}
              isLoading={loading}
              onClick={() => {
                setLoading(true);
                props.setQuery({ cursor: nextPageCursor, pageSize: 20 });
              }}
              text={labels.loadMore}
              theme={BUTTON_THEME_DEFAULT}
            />
          </div>
        ) : undefined}
      </div>
    );
  } else {
    if (state === 'pending') {
      return <Loader />;
    }
    return (
      <div className="ai-data-list">
        <div className="list-header">
          <h4 className="title">{screenText.header}</h4>
          <p>{screenText.description}</p>
        </div>
        <AiDataListEmptyState type={props.type} />
      </div>
    );
  }
};
